/**
 * Where a thread sits in one person's list: active, put aside, or done.
 *
 * Kept apart from the sidebar components because it is the whole product
 * decision of the feature and it has to be testable without rendering
 * anything. Two rules carry it:
 *
 *  - **Urgency outranks both.** A thread that is waiting on the person — an
 *    approval, a question, a stalled delegation — comes back to the list even
 *    while snoozed and even after being marked done. Neither state is a way to
 *    make an agent wait quietly forever, and both are reversible views rather
 *    than facts about the work.
 *  - **Nothing here writes.** Snoozing is stored as a deadline, and a deadline
 *    that has passed reads as "not snoozed" from this moment on, whether or not
 *    the server's sweep has got to it yet. The sweep exists so other clients
 *    and notifications find out; it is not what makes it true here.
 */
export type ThreadInboxSection = "active" | "snoozed" | "settled";

/**
 * Structural on purpose, rather than a `Pick` of one thread type.
 *
 * The three shapes the client carries — the full `Thread`, the `ThreadShell`,
 * and the `SidebarThreadSummary` — disagree about which of these fields they
 * have: only the sidebar summary carries the pending-work flags. Naming the
 * fields here, all optional, lets the same rules run over any of them, and a
 * shape without the flags simply never reads as urgent, which is the correct
 * answer for a caller that does not track urgency in the first place.
 */
export interface ThreadInboxInput {
  readonly settledAt?: string | null | undefined;
  readonly snoozedUntil?: string | null | undefined;
  readonly lastVisitedAt?: string | undefined;
  readonly wokenAt?: string | null | undefined;
  readonly latestTurn: { readonly completedAt?: string | null } | null;
  readonly hasPendingApprovals?: boolean | undefined;
  readonly hasPendingUserInput?: boolean | undefined;
  readonly hasActionableProposedPlan?: boolean | undefined;
}

/**
 * The thread is blocked on this person right now.
 *
 * Deliberately narrow: work merely *running* is not urgent — that is the normal
 * state of a thread somebody snoozed on purpose, and treating it as urgent
 * would make snoozing a busy thread do nothing at all.
 */
export function threadNeedsAttention(thread: ThreadInboxInput): boolean {
  return (
    thread.hasPendingApprovals === true ||
    thread.hasPendingUserInput === true ||
    thread.hasActionableProposedPlan === true
  );
}

/**
 * A turn finished that this person has not looked at since.
 *
 * Only relevant to the settled section: news arriving on a thread someone
 * called done is exactly the case where "done" stopped being true. It does not
 * lift a snooze — the whole point of snoozing a working thread is not to hear
 * about it finishing until the deadline.
 */
export function threadHasUnseenCompletion(thread: ThreadInboxInput): boolean {
  const completedAt = thread.latestTurn?.completedAt;
  if (!completedAt) return false;
  const completedAtMs = Date.parse(completedAt);
  if (Number.isNaN(completedAtMs)) return false;
  if (!thread.lastVisitedAt) return true;
  const lastVisitedAtMs = Date.parse(thread.lastVisitedAt);
  if (Number.isNaN(lastVisitedAtMs)) return true;
  return completedAtMs > lastVisitedAtMs;
}

/** The snooze is still running at `nowMs`, and nothing has overridden it. */
export function isThreadSnoozed(thread: ThreadInboxInput, nowMs: number): boolean {
  if (thread.snoozedUntil == null) return false;
  if (threadNeedsAttention(thread)) return false;
  const untilMs = Date.parse(thread.snoozedUntil);
  // An unparseable deadline is treated as no snooze rather than as a permanent
  // one: losing a thread forever is the worse failure by far.
  if (Number.isNaN(untilMs)) return false;
  return untilMs > nowMs;
}

/**
 * The thread came back from a snooze on its own and has not been opened since.
 *
 * This is what the sidebar mark and the transcript row are both derived from —
 * neither is stored. It has to be derived, in fact: a snooze belongs to one
 * person, so a persisted activity row saying "back from a pause" would tell the
 * whole team who had put the thread aside.
 *
 * "Has not been opened since" is the whole clearing rule. Nothing writes to
 * clear it; the ordinary visit that every thread open already records moves
 * `lastVisitedAt` past `wokenAt` and the mark stops being true.
 */
export function threadReturnedFromSnooze(thread: ThreadInboxInput): boolean {
  if (thread.wokenAt == null) return false;
  // Still snoozed again after having woken: the current pause is what matters,
  // and a mark saying it is back would contradict the row's own countdown.
  if (thread.snoozedUntil != null) return false;
  const wokenAtMs = Date.parse(thread.wokenAt);
  if (Number.isNaN(wokenAtMs)) return false;
  if (!thread.lastVisitedAt) return true;
  const lastVisitedAtMs = Date.parse(thread.lastVisitedAt);
  if (Number.isNaN(lastVisitedAtMs)) return true;
  return wokenAtMs > lastVisitedAtMs;
}

/** The thread is done as far as the list is concerned. */
export function isThreadSettled(thread: ThreadInboxInput): boolean {
  if (thread.settledAt == null) return false;
  if (threadNeedsAttention(thread)) return false;
  // Work finished since it was called done, and nobody has looked. The row goes
  // back up without clearing `settledAt` — undoing it is the person's call, and
  // if they never look, it drops back down on its own once they do.
  return !threadHasUnseenCompletion(thread);
}

export function resolveThreadInboxSection(
  thread: ThreadInboxInput,
  nowMs: number,
): ThreadInboxSection {
  // Snooze is checked first so a thread that is both snoozed and settled reads
  // as snoozed: the deadline is the more specific promise, and it expires.
  if (isThreadSnoozed(thread, nowMs)) return "snoozed";
  if (isThreadSettled(thread)) return "settled";
  return "active";
}

/**
 * Split a list into the three sections, preserving the incoming order.
 *
 * One pass and one `Date.parse` per thread: this runs on every sidebar render
 * for every thread in the installation.
 */
export function partitionThreadsByInboxState<T extends ThreadInboxInput>(
  threads: readonly T[],
  nowMs: number,
): { active: T[]; snoozed: T[]; settled: T[] } {
  const active: T[] = [];
  const snoozed: T[] = [];
  const settled: T[] = [];
  for (const thread of threads) {
    switch (resolveThreadInboxSection(thread, nowMs)) {
      case "snoozed":
        snoozed.push(thread);
        break;
      case "settled":
        settled.push(thread);
        break;
      default:
        active.push(thread);
    }
  }
  return { active, snoozed, settled };
}

/**
 * When the next snooze in this list runs out, or null if none will.
 *
 * The sidebar schedules a single re-render for that instant instead of polling:
 * without it a thread would sit hidden until something else happened to
 * re-render the list, which on a quiet screen can be a long time. The server
 * sweep is what makes it authoritative; this is what makes it prompt.
 */
export function nextSnoozeExpiryMs(
  threads: readonly ThreadInboxInput[],
  nowMs: number,
): number | null {
  let earliest: number | null = null;
  for (const thread of threads) {
    if (!isThreadSnoozed(thread, nowMs)) continue;
    const untilMs = Date.parse(thread.snoozedUntil!);
    if (Number.isNaN(untilMs)) continue;
    if (earliest === null || untilMs < earliest) earliest = untilMs;
  }
  return earliest;
}

export interface SnoozePreset {
  readonly id: string;
  readonly label: string;
  /** Resolves the deadline against the reader's own clock and timezone. */
  readonly resolve: (now: Date) => Date;
}

const atHour = (base: Date, dayOffset: number, hour: number): Date => {
  const next = new Date(base);
  next.setDate(next.getDate() + dayOffset);
  next.setHours(hour, 0, 0, 0);
  return next;
};

/**
 * The offered durations.
 *
 * Resolved on the client on purpose: "tomorrow morning" is a fact about where
 * the reader is sitting, and the server — which may be in another timezone
 * entirely — has no business guessing it. What travels to the server is the
 * absolute instant this produces.
 */
export const SNOOZE_PRESETS: ReadonlyArray<SnoozePreset> = [
  {
    id: "one-hour",
    label: "En una hora",
    resolve: (now) => new Date(now.getTime() + 60 * 60 * 1000),
  },
  {
    id: "this-evening",
    label: "Esta tarde",
    // Already past 18:00 means the offer would be in the past, so it rolls to
    // tomorrow rather than resolving to an instant that expires immediately.
    resolve: (now) => (now.getHours() < 18 ? atHour(now, 0, 18) : atHour(now, 1, 18)),
  },
  {
    id: "tomorrow",
    label: "Mañana por la mañana",
    resolve: (now) => atHour(now, 1, 9),
  },
  {
    id: "next-week",
    label: "La semana que viene",
    // Monday, however far away that is: from a Saturday it is two days, from a
    // Monday it is seven.
    resolve: (now) => atHour(now, ((8 - now.getDay()) % 7 || 7) as number, 9),
  },
];

/**
 * The instant that marks a thread unread again.
 *
 * One millisecond before the last turn finished, so `threadHasUnseenCompletion`
 * flips back to true. Shared between the store, which applies it locally, and
 * the caller that persists it — the two must agree on the value or the next
 * connection would undo the mark.
 *
 * Null when there is no finished turn: there is nothing to be unread about.
 */
export function resolveMarkUnreadVisitedAt(thread: {
  readonly latestTurn: { readonly completedAt?: string | null } | null;
}): string | null {
  const completedAt = thread.latestTurn?.completedAt;
  if (!completedAt) return null;
  const completedAtMs = Date.parse(completedAt);
  if (Number.isNaN(completedAtMs)) return null;
  return new Date(completedAtMs - 1).toISOString();
}

/**
 * The same list with settled threads pushed to the end, order otherwise intact.
 *
 * Sinking them rather than hiding them: a thread someone called done is not
 * archived, and having to reveal a section to undo a misclick is worse than a
 * dimmed row at the bottom of the list. Snoozed threads are not handled here —
 * those are genuinely removed from the list until their deadline, which is what
 * the person asked for.
 */
export function orderThreadsWithSettledLast<T extends ThreadInboxInput>(
  threads: readonly T[],
): T[] {
  const active: T[] = [];
  const settled: T[] = [];
  for (const thread of threads) {
    (isThreadSettled(thread) ? settled : active).push(thread);
  }
  return settled.length === 0 ? [...active] : [...active, ...settled];
}

/**
 * How long is left on a snooze, phrased for a person.
 *
 * Coarse on purpose, and it coarsens as the deadline recedes: seconds matter
 * when a thread is about to come back, days do not. "En 3 días" is the useful
 * answer for something snoozed until next week — "en 3 días, 4 horas y 12
 * minutos" is the same fact, harder to read, and stale a minute later.
 *
 * Past deadlines read as "Volviendo…" rather than as a negative duration: the
 * sweep runs every 30 seconds, so a row can legitimately sit expired for a
 * moment before the event that removes it arrives.
 */
export function formatSnoozeCountdown(untilMs: number, nowMs: number): string {
  const remainingMs = untilMs - nowMs;
  if (remainingMs <= 0) return "Volviendo…";

  // Checked against the raw remainder, not against rounded minutes: 30 seconds
  // rounds up to 1 and would read as "En 1 min" for something coming back in
  // half that.
  if (remainingMs < 60_000) return "En menos de un minuto";
  const minutes = Math.round(remainingMs / 60_000);
  if (minutes < 60) return `En ${minutes} min`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const restMinutes = minutes % 60;
    return restMinutes === 0 ? `En ${hours} h` : `En ${hours} h ${restMinutes} min`;
  }

  const days = Math.round(hours / 24);
  return days === 1 ? "Mañana" : `En ${days} días`;
}

/**
 * The exact instant a snooze ends, for the row that also shows the countdown.
 *
 * The countdown answers "how long do I have"; this answers "when exactly", which
 * is the one people check before deciding whether to bring something back early.
 */
export function formatSnoozeDeadline(untilMs: number, nowMs: number): string {
  const until = new Date(untilMs);
  const time = until.toLocaleTimeString("es", { hour: "2-digit", minute: "2-digit" });
  const startOfToday = new Date(nowMs);
  startOfToday.setHours(0, 0, 0, 0);
  const dayOffset = Math.floor((until.getTime() - startOfToday.getTime()) / 86_400_000);
  if (dayOffset <= 0) return `hoy a las ${time}`;
  if (dayOffset === 1) return `mañana a las ${time}`;
  if (dayOffset < 7) {
    return `el ${until.toLocaleDateString("es", { weekday: "long" })} a las ${time}`;
  }
  return `el ${until.toLocaleDateString("es", { day: "numeric", month: "short" })} a las ${time}`;
}

/**
 * When a snooze ended, phrased for a moment already past.
 *
 * A separate formatter from `formatSnoozeDeadline` rather than a flag on it:
 * that one only ever handles instants ahead of now, and its day arithmetic
 * reads anything earlier as "hoy". Wrong in a tooltip whose only job is to say
 * when the pause ran out.
 */
export function formatSnoozeReturn(wokenAtMs: number, nowMs: number): string {
  const woken = new Date(wokenAtMs);
  const time = woken.toLocaleTimeString("es", { hour: "2-digit", minute: "2-digit" });
  const startOfToday = new Date(nowMs);
  startOfToday.setHours(0, 0, 0, 0);
  const dayOffset = Math.floor((woken.getTime() - startOfToday.getTime()) / 86_400_000);
  if (dayOffset >= 0) return `hoy a las ${time}`;
  if (dayOffset === -1) return `ayer a las ${time}`;
  if (dayOffset > -7) {
    return `el ${woken.toLocaleDateString("es", { weekday: "long" })} a las ${time}`;
  }
  return `el ${woken.toLocaleDateString("es", { day: "numeric", month: "short" })} a las ${time}`;
}

export type CustomSnoozeError = "incomplete" | "unparseable" | "past";

/**
 * Turn the date and time a person typed into an instant, or say why it cannot be.
 *
 * Built through the `Date` constructor from parts rather than by parsing a
 * string: `new Date("2026-01-02T09:00")` is read as local time by every current
 * engine, but the same value with a `Z`, or with only a date, is read as UTC —
 * a difference of hours, silently, on exactly the input people type here.
 *
 * A deadline in the past is refused rather than clamped. Somebody who typed
 * yesterday's date meant something, and snoozing until an instant that has
 * already passed would put the thread back in the list at once, which reads as
 * the feature being broken.
 */
export function resolveCustomSnooze(input: {
  readonly date: string;
  readonly time: string;
  readonly nowMs: number;
}): { ok: true; date: Date } | { ok: false; error: CustomSnoozeError } {
  const date = input.date.trim();
  const time = input.time.trim();
  if (date.length === 0 || time.length === 0) return { ok: false, error: "incomplete" };

  const dateParts = date.split("-").map(Number);
  const timeParts = time.split(":").map(Number);
  const [year, month, day] = dateParts;
  const [hour, minute] = timeParts;
  if (
    dateParts.length !== 3 ||
    timeParts.length < 2 ||
    [year, month, day, hour, minute].some((part) => part === undefined || Number.isNaN(part))
  ) {
    return { ok: false, error: "unparseable" };
  }

  const resolved = new Date(year!, month! - 1, day!, hour!, minute!, 0, 0);
  if (Number.isNaN(resolved.getTime())) return { ok: false, error: "unparseable" };
  // Rejects a rolled-over date (31 de febrero) rather than accepting the March
  // day the constructor would silently produce.
  if (resolved.getMonth() !== month! - 1 || resolved.getDate() !== day!) {
    return { ok: false, error: "unparseable" };
  }
  if (resolved.getTime() <= input.nowMs) return { ok: false, error: "past" };
  return { ok: true, date: resolved };
}

/**
 * The instant to record as "read" once a turn has finished, or null when there
 * is nothing to write.
 *
 * Never behind the turn it acknowledges. The reader's browser and the server do
 * not share a clock, and the completion instant comes from the server: with a
 * browser running even a few seconds late, `Date.now()` does not reach
 * `completedAt`, so the write would not clear the condition that asked for it
 * and the caller would be told to write again on the very next render. That is
 * not hypothetical — it is what made the chat crash with React's "maximum
 * update depth exceeded" while an agent was finishing turn after turn.
 *
 * Null when the turn has no usable completion instant, or when the reader has
 * already been here since it finished.
 */
export function resolveTurnVisitAcknowledgement(input: {
  readonly turnCompletedAt: string | null | undefined;
  readonly lastVisitedAt: string | null | undefined;
  /** Injectable for tests; defaults to the reader's clock. */
  readonly now?: number;
}): string | null {
  if (!input.turnCompletedAt) return null;
  const turnCompletedAtMs = Date.parse(input.turnCompletedAt);
  if (Number.isNaN(turnCompletedAtMs)) return null;

  const lastVisitedAtMs = input.lastVisitedAt ? Date.parse(input.lastVisitedAt) : NaN;
  if (!Number.isNaN(lastVisitedAtMs) && lastVisitedAtMs >= turnCompletedAtMs) return null;

  return new Date(Math.max(input.now ?? Date.now(), turnCompletedAtMs)).toISOString();
}

/**
 * A thread plus the one link needed to know whether somebody put its parent
 * aside. Structural for the same reason as `ThreadInboxInput`: the sidebar
 * summary is the only shape that carries the parent link, and a caller without
 * it simply has no trees to prune.
 */
export interface ThreadInboxTreeInput extends ThreadInboxInput {
  readonly id: string;
  readonly parentThreadId?: string | null | undefined;
}

/**
 * Drop the threads whose parent — or any ancestor — is snoozed.
 *
 * Snoozing hides a row, and the per-project list nests children under it. Take
 * only the parent out and `buildProjectThreadTree` no longer finds a parent for
 * the children, so it promotes every delegation to a root row: the person who
 * put one thread aside gets four unexplained `agente (delegate)` rows in its
 * place, which is worse than not snoozing at all. Pruning the subtree is what
 * makes the deadline mean what it says.
 *
 * The exception is the module's standing rule that urgency outranks snoozing: a
 * descendant blocked on this person is not buried, and the ancestors above it
 * come back with it — showing an urgent child as an orphan root would recreate
 * the very thing this prunes. It is only the chain up to that child that
 * returns; unrelated siblings stay hidden until the deadline.
 */
export function filterThreadsWithSnoozedAncestor<T extends ThreadInboxTreeInput>(
  threads: readonly T[],
  nowMs: number,
): T[] {
  const threadById = new Map<string, T>(threads.map((thread) => [thread.id, thread] as const));
  const hiddenById = new Map<string, boolean>();

  const isHidden = (thread: T, seen: Set<string>): boolean => {
    const cached = hiddenById.get(thread.id);
    if (cached !== undefined) return cached;
    // A cycle cannot happen in a parent chain the server builds, but resolving
    // one recursively would hang the sidebar, and losing a thread forever is
    // the worse failure, so it reads as visible.
    if (seen.has(thread.id)) return false;
    seen.add(thread.id);

    let hidden: boolean;
    if (isThreadSnoozed(thread, nowMs)) {
      hidden = true;
    } else {
      const parentThreadId = thread.parentThreadId ?? null;
      const parent = parentThreadId != null ? threadById.get(parentThreadId) : undefined;
      hidden = parent !== undefined && isHidden(parent, seen);
    }

    hiddenById.set(thread.id, hidden);
    return hidden;
  };

  const forcedVisibleIds = new Set<string>();
  for (const thread of threads) {
    if (!threadNeedsAttention(thread)) continue;
    if (!isHidden(thread, new Set())) continue;
    let current: T | undefined = thread;
    while (current && !forcedVisibleIds.has(current.id)) {
      forcedVisibleIds.add(current.id);
      const parentThreadId: string | null = current.parentThreadId ?? null;
      current = parentThreadId != null ? threadById.get(parentThreadId) : undefined;
    }
  }

  return threads.filter(
    (thread) => !isHidden(thread, new Set()) || forcedVisibleIds.has(thread.id),
  );
}
